import * as THREE from 'three';
import {SketchCornerOperationContext} from './SketchCornerOperationContext';
import type {
  SketchCornerAnalysis,
  SketchCornerOperationOptions,
  SketchCornerOperationResult,
} from './SketchCornerTypes';
import {SKETCH_GEOMETRY_TOLERANCE} from './SketchCornerTypes';

/**
 * 草图圆角执行服务。
 * 职责：
 * 1. 校验圆角参数与可修剪范围；
 * 2. 计算圆弧几何并创建圆弧实体；
 * 3. 完成旧角点替换与清理。
 */
export class SketchFilletOperationService {
  /** 圆角上下文。 */
  private readonly context: SketchCornerOperationContext;

  /**
   * 构造函数。
   * @param context 圆角上下文。
   */
  public constructor(context: SketchCornerOperationContext) {
    this.context = context;
  }

  /**
   * 执行草图圆角。
   * @param cornerAnalysis 角点解析结果。
   * @param angle 两线夹角。
   * @param options 操作参数。
   * @param isConstruction 是否构造线。
   * @returns 执行结果。
   */
  public execute(
    cornerAnalysis: SketchCornerAnalysis,
    angle: number,
    options: SketchCornerOperationOptions,
    isConstruction: boolean,
  ): SketchCornerOperationResult {
    const referencePlane = cornerAnalysis.firstUsage.lineData.line.referencePlane;
    const radius = Number(options.radius ?? 10);
    if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE) {
      return {success: false, message: '圆角半径必须大于 0'};
    }

    const trimDistance = radius / Math.tan(angle / 2);
    if (!Number.isFinite(trimDistance) || trimDistance <= SKETCH_GEOMETRY_TOLERANCE) {
      return {success: false, message: '圆角半径与夹角组合无效'};
    }
    if (
      !this.context.isTrimDistanceWithinLineRange(trimDistance, cornerAnalysis.firstUsage)
      || !this.context.isTrimDistanceWithinLineRange(trimDistance, cornerAnalysis.secondUsage)
    ) {
      return {success: false, message: '圆角半径过大，超出线段可修剪范围'};
    }

    const filletGeometry = this.context.resolveFilletGeometry(cornerAnalysis, angle, radius);
    if (!filletGeometry) {
      return {success: false, message: '圆角几何计算失败'};
    }

    const arcStartPoint = this.context.createCornerControlPoint(
      filletGeometry.trimPoint1World,
      undefined,
      referencePlane,
    );
    const arcFinishPoint = this.context.createCornerControlPoint(
      filletGeometry.trimPoint2World,
      undefined,
      referencePlane,
    );
    const firstOldCornerPoint = cornerAnalysis.firstUsage.cornerPoint;
    const secondOldCornerPoint = cornerAnalysis.secondUsage.cornerPoint;
    this.context.replaceLineCornerControlPoint(
      cornerAnalysis.firstUsage.lineData.line,
      cornerAnalysis.firstUsage.cornerRole,
      arcStartPoint,
    );
    this.context.replaceLineCornerControlPoint(
      cornerAnalysis.secondUsage.lineData.line,
      cornerAnalysis.secondUsage.cornerRole,
      arcFinishPoint,
    );
    this.context.removeDetachedPoint(firstOldCornerPoint);
    this.context.removeDetachedPoint(secondOldCornerPoint);

    const {arc: arcLine, controlPoints: arcControlPoints} = this.context.createSketchArcEntity(
      isConstruction,
      filletGeometry.arcPoints,
      {
        startPoint: arcStartPoint,
        finishPoint: arcFinishPoint,
        center: filletGeometry.centerWorld,
        radius,
        startAngle: filletGeometry.startAngle,
        endAngle: filletGeometry.endAngle,
        clockwise: filletGeometry.clockwise,
        referencePlane,
      },
    );
    //------ 圆角默认约束支持 开始------
    // 圆角创建后自动挂载 2 条相切约束；半径由自动半径尺寸的关系视图锁定（对齐 SW）。
    this.context.bindDefaultConstraintsForFilletArc(
      arcLine,
      cornerAnalysis.firstUsage.lineData.line,
      cornerAnalysis.secondUsage.lineData.line,
    );
    //------ 圆角默认约束支持 结束------
    return {
      success: true,
      message: '草图圆角已创建',
      createdObject: arcLine,
      controlPoints: arcControlPoints,
    };
  }

  //------ 直线圆弧草图圆角执行 开始------
  /**
   * 执行直线—圆弧草图圆角。
   *
   * 设计说明：
   * 1. 几何解析在调用前已经完成，本方法只负责模型拓扑与约束提交；
   * 2. 源圆弧通过对象级端点替换 API 更新，保持原圆心与半径；
   * 3. 两个新切点分别由源实体与新圆角共享，重合关系由共享点拓扑表达；
   * 4. 操作应在草图运行时历史事务内调用，异常由事务统一恢复整组草图。
   *
   * @param geometry 直线—圆弧圆角几何结果。
   * @param isConstruction 新圆角是否为构造几何。
   * @returns 执行结果。
   */
  public executeLineArc(
    geometry: SketchLineArcFilletGeometryResult,
    isConstruction: boolean,
  ): SketchCornerOperationResult {
    const referencePlane = geometry.line.referencePlane;
    if (
      !Number.isFinite(geometry.radius)
      || geometry.radius <= SKETCH_GEOMETRY_TOLERANCE
      || geometry.arcPoints.length < 2
    ) {
      return {success: false, message: '直线—圆弧圆角几何数据无效'};
    }

    // 为两处切点创建独立控制点，避免直接移动原共享角点并连带改变其它实体。
    const lineTangentPoint = this.context.createCornerControlPoint(
      geometry.lineTangentWorld,
      undefined,
      referencePlane,
    );
    const arcTangentPoint = this.context.createCornerControlPoint(
      geometry.arcTangentWorld,
      undefined,
      referencePlane,
    );

    // 先替换两条源实体的角点侧端点，再清理已经完全脱离拓扑的旧角点。
    this.context.replaceLineCornerControlPoint(
      geometry.line,
      geometry.lineCornerRole,
      lineTangentPoint,
    );
    this.context.replaceArcCornerControlPoint(
      geometry.sourceArc,
      geometry.arcCornerRole,
      arcTangentPoint,
      geometry.nextSourceArcDefinition,
    );
    this.context.removeDetachedPoint(geometry.lineOldCornerPoint);
    this.context.removeDetachedPoint(geometry.arcOldCornerPoint);

    // 新圆角以“直线切点 -> 圆弧切点”的稳定顺序创建，预览与提交使用同一组角度数据。
    const {arc: filletArc, controlPoints} = this.context.createSketchArcEntity(
      isConstruction,
      geometry.arcPoints,
      {
        startPoint: lineTangentPoint,
        finishPoint: arcTangentPoint,
        center: geometry.centerWorld,
        radius: geometry.radius,
        startAngle: geometry.filletStartAngle,
        endAngle: geometry.filletEndAngle,
        clockwise: geometry.filletClockwise,
        referencePlane,
      },
    );

    // 默认关系为直线—圆角相切与源圆弧—圆角相切；半径由自动半径尺寸锁定（对齐 SW）。
    this.context.bindDefaultConstraintsForFilletArc(
      filletArc,
      geometry.line,
      geometry.sourceArc,
    );
    return {
      success: true,
      message: '直线—圆弧草图圆角已创建',
      createdObject: filletArc,
      controlPoints,
    };
  }
  //------ 直线圆弧草图圆角执行 结束------
}
